// Global
import React from 'react';
import { LinkField } from '@sitecore-jss/sitecore-jss-react';
import { classnames, TTailwindString } from 'tailwindcss-classnames';

// Lib
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { OnLinkDataLayerPush } from 'lib/util/gtm-datalayer';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';

const fontSemiBold = `font-semibold`;

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  | 'link'
  | 'link-green'
  | 'link-secondary'
  | 'heroPrimary'
  | 'heroSecondary'
  | 'none';

export type ButtonTWClasses =
  | 'btn'
  | 'btn-primary'
  | 'btn-secondary'
  | 'btn-tertiary'
  | 'btn-destructive'
  | 'btn-link';

type ContainedByPercentType = 100 | 75 | 50 | 25;

export interface WithButtonProps extends React.HTMLAttributes<HTMLElement> {
  containedBy?: ContainedByPercentType;
  customIcon?: 'arrow-right' | 'arrow-down' | false;
  disabled?: boolean;
  editable?: boolean;
  isCoverLink?: boolean;
  linkField?: LinkField;
  onClick?: (event: React.TouchEvent | React.MouseEvent) => void;
  onGtmPush?: OnLinkDataLayerPush;
  srOnlyText?: string;
  suppressNewTabIcon?: boolean | 'true' | 'false';
  suppressLinkText?: boolean | 'true' | 'false';
  text?: string;
  type?: 'button' | 'submit';
  variant?: ButtonVariant;
}

const anchorBase = classnames(
  fontSemiBold,
  'text-theme-btn-link-text',
  'hover:text-theme-btn-link-text-hover'
);

const buttonBase = classnames(
  'rounded-full',
  fontSemiBold,
  'text-base',
  'leading-3',
  'inline-block',
  'px-4',
  'py-2',
  'sm:px-6',
  'sm:py-btnLg',
  'sm:leading-5',
  'focus:outline',
  'focus:outline-4',
  'focus:outline-blue-morning-tint'
);

const variants: Record<ButtonVariant, TTailwindString> = {
  primary: classnames(
    buttonBase,
    'bg-theme-btn-primary-bg',
    'hover:bg-theme-btn-primary-bg-hover',
    'text-theme-btn-primary-text',
    'hover:text-theme-btn-primary-text-hover'
  ),
  secondary: classnames(
    buttonBase,
    'bg-theme-btn-secondary-bg',
    'hover:bg-theme-btn-secondary-bg-hover',
    'text-theme-btn-secondary-text',
    'hover:text-theme-btn-secondary-text-hover',
    'border-2',
    'border-theme-btn-secondary-border',
    'hover:border-theme-btn-secondary-border-hover'
  ),
  tertiary: classnames(
    buttonBase,
    'bg-theme-btn-tertiary-bg',
    'hover:bg-theme-btn-tertiary-bg-hover',
    'text-theme-btn-tertiary-text',
    'hover:text-theme-btn-tertiary-text-hover'
  ),
  destructive: classnames(
    buttonBase,
    'bg-theme-btn-destructive-bg',
    'text-theme-btn-primary-text',
    'hover:bg-theme-btn-destructive-bg-hover'
  ),
  link: classnames(
    anchorBase,
    fontSemiBold,
    'text-theme-btn-link-text',
    'inline-flex',
    'items-center',
    'hover:underline',
    'hover:text-theme-btn-link-text-hover',
    'focus-visible:text-theme-btn-link-text-focus',
    'focus-visible:outline',
    'focus-visible:outline-2',
    'focus-visible:outline-theme-btn-link-focus-outline',
    'focus-visible:rounded',
    'active:text-theme-btn-link-text-active',
    'disabled:text-gray-midGray',
    'aria-disabled:text-gray-midGray',
    'aria-disabled:pointer-events-none'
  ),
  'link-green': classnames(
    fontSemiBold,
    'text-dayglow',
    'inline-flex',
    'items-center',
    'hover:underline',
    'hover:text-dayglow-tint',
    'active:text-dayglow-tint',
    'disabled:text-gray-midGray',
    'aria-disabled:text-gray-midGray',
    'aria-disabled:pointer-events-none'
  ),
  'link-secondary': classnames(
    fontSemiBold,
    'text-theme-btn-link-secondary-text',
    'inline-flex',
    'items-center',
    'hover:underline',
    'hover:text-theme-btn-link-secondary-text-hover',
    'focus-visible:text-theme-btn-link-secondary-text-focus',
    'focus-visible:outline',
    'focus-visible:outline-2',
    'focus-visible:outline-theme-btn-link-secondary-focus-outline',
    'focus-visible:rounded',
    'active:text-theme-btn-link-secondary-text-active',
    'disabled:text-gray-midGray',
    'aria-disabled:text-gray-midGray',
    'aria-disabled:pointer-events-none'
  ),
  heroPrimary: classnames(
    buttonBase,
    'bg-transparent',
    'border-2',
    'w-fit',
    'line-clamp-1',
    'disabled:text-gray-midGray',
    'aria-disabled:text-gray-midGray',
    'aria-disabled:pointer-events-none'
  ),
  heroSecondary: classnames(
    fontSemiBold,
    'text-theme-btn-link-text',
    'w-fit',
    'line-clamp-1',
    'underline',
    'focus-visible:outline',
    'focus-visible:outline-2',
    'rounded-sm',
    'disabled:text-gray-midGray',
    'aria-disabled:text-gray-midGray',
    'aria-disabled:pointer-events-none'
  ),
  none: classnames(undefined),
};

const getDisplayName = (WrappedComponent: React.FC) => {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
};

const WithButton = (Component: React.FC<WithButtonProps>) => {
  const theComponent = Component;
  theComponent.displayName = `WithButton(${getDisplayName(Component)})`;

  return ({
    text,
    srOnlyText,
    variant,
    onClick,
    className,
    customIcon,
    disabled,
    isCoverLink,
    ...props
  }: WithButtonProps): React.JSX.Element => {
    // This is a false-positive with ESLint thinking this is a callback
    // when it's a higher order component function
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isExperienceEditor = useExperienceEditor();

    const linkFieldValue = props?.linkField?.value;

    // Validate our button variant
    let buttonVariant: TTailwindString;
    let btnPrefix: TTailwindString;
    if (variant === 'none') {
      buttonVariant = variants.none;
      btnPrefix = variants.none;
    } else {
      buttonVariant =
        typeof variant !== 'undefined' && !!variants[variant]
          ? variants[variant]
          : variants['primary'];
      btnPrefix = classnames('btn');
    }

    const utilityClasses: TTailwindString = (className ?? '') as TTailwindString;

    const handleClick = (e: React.TouchEvent | React.MouseEvent): boolean | void => {
      if (disabled) {
        e.preventDefault();
        return false;
      }
      return onClick ? onClick(e) : true;
    };

    // Set the target for all external links to be _blank
    if (linkFieldValue?.target !== '_blank' && linkFieldValue?.linktype === 'external') {
      linkFieldValue.target = '_blank';
    }

    return (
      <>
        {isExperienceEditor && linkFieldValue ? (
          // In experience editor, do not pass any children but retain basic styling
          // so that double components do not appear when using <Link>, but only
          // if there is a linkFieldValue, otherwise it's not a <Link> component.
          <Component
            className={classnames(btnPrefix, buttonVariant, utilityClasses)}
            onClick={handleClick}
            aria-disabled={disabled ? true : undefined}
            srOnlyText={srOnlyText && linkFieldValue ? srOnlyText : undefined}
            {...props}
          />
        ) : (
          <Component
            className={classnames(btnPrefix, buttonVariant, utilityClasses)}
            onClick={handleClick}
            aria-disabled={disabled ? true : undefined}
            srOnlyText={srOnlyText && linkFieldValue ? srOnlyText : undefined}
            {...props}
          >
            {props.children}
            {!linkFieldValue && (
              <>
                {text}
                {srOnlyText ? <span className="sr-only">{srOnlyText}</span> : ''}
              </>
            )}

            {customIcon && (
              <SvgIcon
                icon={customIcon}
                size="em"
                viewBoxSize="0 0 48 48"
                className={classnames('ml-2', 'shrink-0')}
              />
            )}
            {isCoverLink && <span className={classnames('absolute', 'inset-0')} />}
          </Component>
        )}
      </>
    );
  };
};

WithButton.displayName = 'WithButton';
export default WithButton;
